import html2canvas from "html2canvas";

const A4_WIDTH_PT = 595.28;
const A4_HEIGHT_PT = 841.89;
const REPORT_WIDTH_PX = 794;

const textEncoder = new TextEncoder();

const concatBytes = (parts) => {
  const chunks = parts.map((part) =>
    typeof part === "string" ? textEncoder.encode(part) : part
  );
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  chunks.forEach((chunk) => {
    out.set(chunk, offset);
    offset += chunk.length;
  });
  return out;
};

const jpegBytesFromCanvas = (canvas) => {
  const dataUrl = canvas.toDataURL("image/jpeg", 0.86);
  const base64 = dataUrl.split(",")[1] || "";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const sliceCanvasToJpegPages = (canvas) => {
  const pageHeightPx = Math.max(
    1,
    Math.round(canvas.width * (A4_HEIGHT_PT / A4_WIDTH_PT))
  );
  const pages = [];
  for (let y = 0; y < canvas.height; y += pageHeightPx) {
    const sliceHeight = Math.min(pageHeightPx, canvas.height - y);
    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = canvas.width;
    pageCanvas.height = sliceHeight;
    const ctx = pageCanvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
    ctx.drawImage(
      canvas,
      0,
      y,
      canvas.width,
      sliceHeight,
      0,
      0,
      canvas.width,
      sliceHeight
    );
    pages.push({
      width: pageCanvas.width,
      height: pageCanvas.height,
      bytes: jpegBytesFromCanvas(pageCanvas),
    });
  }
  return pages;
};

const buildImagePdf = (jpegPages) => {
  const pages = jpegPages.filter((page) => page?.bytes?.length);
  if (!pages.length) {
    throw new Error("فشل إنشاء صفحات PDF");
  }

  const parts = [];
  const offsets = [0];
  const currentOffset = () => parts.reduce((sum, part) => sum + part.length, 0);
  const push = (data) => {
    parts.push(typeof data === "string" ? textEncoder.encode(data) : data);
  };

  push("%PDF-1.4\n");

  const addObject = (body) => {
    offsets.push(currentOffset());
    const id = offsets.length - 1;
    push(`${id} 0 obj\n${body}\nendobj\n`);
    return id;
  };

  addObject("<< /Type /Catalog /Pages 2 0 R >>");

  const pageObjectIds = pages.map((_, index) => 5 + index * 3);
  addObject(
    `<< /Type /Pages /Kids [${pageObjectIds
      .map((id) => `${id} 0 R`)
      .join(" ")}] /Count ${pages.length} >>`
  );

  pages.forEach((page, index) => {
    const imageId = 3 + index * 3;
    const contentId = 4 + index * 3;
    const pageId = 5 + index * 3;

    offsets.push(currentOffset());
    push(`${imageId} 0 obj\n`);
    push(
      `<< /Type /XObject /Subtype /Image /Width ${page.width} /Height ${page.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${page.bytes.length} >>\nstream\n`
    );
    push(page.bytes);
    push("\nendstream\nendobj\n");

    const imgWidth = A4_WIDTH_PT;
    const imgHeight = A4_WIDTH_PT * (page.height / page.width);
    const y = Math.max(0, A4_HEIGHT_PT - imgHeight);
    const stream = `q ${imgWidth.toFixed(2)} 0 0 ${imgHeight.toFixed(2)} 0 ${y.toFixed(2)} cm /Im1 Do Q`;

    offsets.push(currentOffset());
    push(
      `${contentId} 0 obj\n<< /Length ${stream.length} >>\nstream\n${stream}\nendstream\nendobj\n`
    );

    offsets.push(currentOffset());
    push(
      `${pageId} 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${A4_WIDTH_PT} ${A4_HEIGHT_PT}] /Resources << /XObject << /Im1 ${imageId} 0 R >> >> /Contents ${contentId} 0 R >>\nendobj\n`
    );
  });

  const xrefOffset = currentOffset();
  let xref = `xref\n0 ${offsets.length}\n`;
  xref += "0000000000 65535 f \n";
  for (let i = 1; i < offsets.length; i += 1) {
    xref += `${String(offsets[i]).padStart(10, "0")} 00000 n \n`;
  }
  push(xref);
  push(
    `trailer\n<< /Size ${offsets.length} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`
  );

  return concatBytes(parts);
};

const triggerPdfDownload = (bytes, fileName) => {
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName.endsWith(".pdf") ? fileName : `${fileName}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1500);
};

const waitFrames = () =>
  new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

/**
 * Render HTML to a downloadable A4 PDF and save it on the user's device.
 */
export const downloadHtmlAsPdf = async ({ html, fileName }) => {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = [
    "position:fixed",
    "left:-10000px",
    "top:0",
    `width:${REPORT_WIDTH_PX}px`,
    "height:800px",
    "border:0",
    "background:#fff",
  ].join(";");
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  if (!doc) {
    iframe.remove();
    throw new Error("فشل تجهيز ملف PDF");
  }

  doc.open();
  doc.write(
    `<!DOCTYPE html><html lang="ar" dir="rtl"><head><meta charset="UTF-8" /></head><body style="margin:0;background:#fff;">${html}</body></html>`
  );
  doc.close();

  try {
    await waitFrames();
    if (doc.fonts?.ready) {
      try {
        await doc.fonts.ready;
      } catch {
        // ignore
      }
    }

    const root = doc.querySelector(".pdf-root") || doc.body;
    iframe.style.height = `${Math.max(root.scrollHeight + 32, 400)}px`;
    await waitFrames();

    const blocks = [...doc.querySelectorAll(".pdf-block")];
    const targets = blocks.length ? blocks : [root];
    const jpegPages = [];

    for (const target of targets) {
      const canvas = await html2canvas(target, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        windowWidth: REPORT_WIDTH_PX,
        width: Math.max(target.scrollWidth, REPORT_WIDTH_PX - 32),
      });
      jpegPages.push(...sliceCanvasToJpegPages(canvas));
    }

    triggerPdfDownload(buildImagePdf(jpegPages), fileName);
    return jpegPages.length;
  } finally {
    iframe.remove();
  }
};
